import { Stack, Typography } from '@mui/material'
import React from 'react'

import { ChipWithTooltip } from 'components/common/chips/ChipWithTooltip'
import { MUIExternalLink } from 'components/common/MUIExternalLink'
import PageSection from 'components/common/PageSection'
import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import Icon from 'components/Icon'
import { ObservabilityToolIcon } from 'components/status-checks/common/ObservabilityToolIcon'
import { EditMonitorDetailsDrawer } from 'components/status-checks/edit/EditMonitorDetailsDrawer'
import {
  formatStatusCheckTag,
  getMonitorPageUrl
} from 'components/status-checks/status-checks-form-utils'
import { useFeatureFlags } from 'hooks/use-feature-flags'
import { useGetDatadogMonitor } from 'queries/monitors/use-get-datadog-monitor'
import { getMessageFromResponseOrError } from 'utils/strings'

import type { ServiceStatusCheckFromStatusCheck } from 'types/api/status-checks/status-check'

export const EditDatadogMonitorDetails: React.FC<{
  monitorId: string
  statusCheck: ServiceStatusCheckFromStatusCheck
}> = ({ monitorId, statusCheck }) => {
  const { multiPniRouting: isMultiPniRoutingEnabled } = useFeatureFlags()

  const {
    domain,
    isPrivateNetworkIntegration,
    name: datadogIntegrationName,
    type: integrationType,
    url: integrationUrl
  } = statusCheck.integration

  const requestParams = {
    datadogIntegrationName,
    ...(domain && domain !== 'US1' && { domain })
  }

  const {
    data: datadogMonitor,
    error: errorFetchingDatadogMonitor,
    isFetching: isFetchingDatadogMonitor,
    isError: isErrorFetchingDatadogMonitor
  } = useGetDatadogMonitor(monitorId, requestParams)

  const monitorName = datadogMonitor?.name
  const monitorTags = datadogMonitor?.tags ?? []

  const canEditTags = isMultiPniRoutingEnabled && isPrivateNetworkIntegration

  return (
    <PageSection
      hasError={isErrorFetchingDatadogMonitor}
      isLoading={isFetchingDatadogMonitor}
      isLoadingOverlayFullScreen={false}
      loadingBoxProps={{
        color: 'grey.300',
        sx: { maxWidth: 250 }
      }}
      renderError={() => (
        <Typography color="error" variant="body2">
          {getMessageFromResponseOrError(
            errorFetchingDatadogMonitor,
            'Failed to load Datadog monitor details.'
          )}
        </Typography>
      )}
    >
      {monitorName && (
        <Stack direction="row" sx={{ gap: 2, marginBottom: 1 }}>
          <Stack sx={{ gap: 2 }}>
            <Stack direction="row" sx={{ gap: 1 }}>
              <ObservabilityToolIcon integration={statusCheck.integration} />
              <TruncatedTextWithTooltip
                tooltipProps={{ title: `Monitor: ${monitorName}` }}
              >
                {monitorName}
              </TruncatedTextWithTooltip>
              {integrationUrl && (
                <MUIExternalLink
                  href={getMonitorPageUrl(integrationUrl, integrationType)}
                >
                  <Icon icon="arrowUpRightFromSquare" />
                </MUIExternalLink>
              )}
            </Stack>
            <Stack
              direction="row"
              sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}
            >
              <Typography>Tags: </Typography>
              {monitorTags.map((tag) => {
                const label = formatStatusCheckTag(tag)

                return (
                  <ChipWithTooltip
                    key={tag}
                    label={label}
                    size="small"
                    tooltipProps={{ title: label }}
                  />
                )
              })}
              {canEditTags && (
                <EditMonitorDetailsDrawer
                  monitorTags={monitorTags}
                  statusCheck={statusCheck}
                />
              )}
            </Stack>
          </Stack>
        </Stack>
      )}
    </PageSection>
  )
}
